import './app.less';
import { Fragment } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { PrivateRoute } from '../components/PrivateRoute';
import { PublicRoute } from '../components/PublicRoute';
import { PermifyProvider } from "@permify/react-role";
import ValidationPage from '../ValidationPage/ValidationPage.jsx';
import TestPage from '../TestPage/TestPage';
import RevisionPage from '../RevisionPage/RevisionPage';
import SuccessPage from '../SuccessPage/SuccessPage';
import TestRevisionPage from '../TestRevisionPage/TestRevisionPage';
import ReservationSearchPage from './ReservationPage/ReservationSearchPage';
import ReservationWebPage from './ReservationPage/ReservationWebPage';
import ReservationByDatePage from './ReservationPage/ReservationByDatePage';
import ReservationSettingsPage from './ReservationPage/ReservationSettingsPage';
import ReservationCreatePage from './ReservationPage/ReservationCreatePage';
import ReservationDetailPage from './ReservationPage/ReservationDetailPage';
import ReservationForecastPage from './ReservationPage/ReservationForecastPage';
import AdmissionPage from './AdmissionPage/AdmissionPage';
import ConsentPopupPage from './AdmissionPage/ConsentPopupPage';
import PatientValidationPage from './AdmissionPage/PatientValidationPage';
import CameraPopupPage from './AdmissionPage/CameraPopupPage';
import MedicalConsultationPage from './MedicalConsultationPage/MedicalConsultationPage';
import MedicalConsultationDetail from './MedicalConsultationPage/MedicalConsultationDetail';
import MedicalConsultationReportPage from './MedicalConsultationPage/MedicalConsultationReportPage';
import ExamPage from './ExamPage/ExamPage';
import ExamDetailPage from './ExamPage/ExamDetailPage';
import ExamSignaturePage from './ExamPage/ExamSignaturePage';
import PatientReevaluationList from './ReservationPage/PatientReevaluationList';
import PatientReevaluationSettings from './ReservationPage/PatientReevaluationSettings';
import PatientPage from './AdmissionPatientPage/PatientPage';
import PatientsPage from './PatientPage/PatientsPage';
import PatientDetailPage from './PatientPage/PatientDetailPage';
import PatientSettingsPage from './PatientPage/PatientSettingsPage';
import { getCookie } from '../services/cookies';
import PatientLandingPage from '../PatientLandingPage/PatientLandingPage';
import HealthSurveyPage from '../HealthSurveyPage/HealthSurveyPage';
import SurveySummaryPage from '../HealthSurveySummary/SurveySummaryPage';
import PatientAttentionStatePage from './../PatientAttentionStatePage/PatientAttentionStatePage';
import { I18nextProvider } from 'react-i18next';
import i18next from 'i18next';
import './i18n';
import { BoxProvider } from '../context/BoxContext';

const App = () => {
  const roles = getCookie('roles');
  let navigateElement;

  const { hostname, pathname } = window.location;

  if (hostname.includes('board.cmtsalud') && !pathname.includes('/mis-examenes')) {
    const countryCode = hostname.includes('.pe') ? 'PE' : 'CL';
    window.location.href = `/mis-examenes/${countryCode}`;
    return null;
  }

  if (roles && roles.includes('doctor')) {
    navigateElement = <Navigate to={`/consulta-medica/buscar`} />;
  } else if (roles && (roles.filter((rol) => ['equipo_medico', 'equipo_medico_externo'].includes(rol))?.length)) {
    navigateElement = <Navigate to={`/examenes/buscar`} />;
  } else {
    navigateElement = <Navigate to={`/reservas/fecha/`} />;
  }

  return (
    <PermifyProvider>
      <I18nextProvider i18n={i18next}>
        <BoxProvider>
          <BrowserRouter>
            <Routes>
              <Route
                path="/"
                element={
                  <Fragment>
                    {navigateElement}
                  </Fragment>
                }
              />
              {/* Public Sage */}
              <Route exact path="/examenes/psicologia/:countryCode" element={<PublicRoute><ValidationPage /></PublicRoute>} />
              <Route exact path="/examenes/psicologia/:countryCode/test" element={<PublicRoute><TestPage /></PublicRoute>} />
              <Route exact path="/examenes/psicologia/:countryCode/revision" element={<PublicRoute><RevisionPage /></PublicRoute>} />
              <Route exact path="/examenes/psicologia/:countryCode/success" element={<PublicRoute><SuccessPage /></PublicRoute>} />
              <Route exact path="/examenes/psicologia/testRevision/:id" element={<PublicRoute><TestRevisionPage /></PublicRoute>} />
              <Route exact path="/mis-examenes/:countryCode" element={<PublicRoute><PatientLandingPage /></PublicRoute>} />
              <Route exact path="/mis-examenes/:countryCode/estado" element={<PublicRoute><PatientAttentionStatePage /></PublicRoute>} />
              <Route exact path="/mis-examenes/:countryCode/encuesta-salud" element={<PublicRoute><HealthSurveyPage /></PublicRoute>} />
              <Route exact path="/mis-examenes/:countryCode/encuesta-salud/sumario" element={<PublicRoute><SurveySummaryPage /></PublicRoute>} />

              {/* Private Sage */}
              <Route exact path="/reservas/buscar" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'administracion', 'direccion_medica']}>
                  <ReservationSearchPage />
                </PrivateRoute>
              } />
              <Route exact path="/reservas/web" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'administracion', 'direccion_medica']}>
                  <ReservationWebPage />
                </PrivateRoute>
              } />

              <Route exact path="/reservas/fecha/:date" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'administracion',
                  'equipo_psicologia', 'doctor', 'direccion_medica', 'equipo_medico']}>
                  <ReservationByDatePage />
                </PrivateRoute>
              } />
              <Route exact path="/reservas/fecha/" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'administracion',
                  'equipo_psicologia', 'doctor', 'direccion_medica', 'equipo_medico']}>
                  <ReservationByDatePage />
                </PrivateRoute>
              } />

              <Route exact path="/reservas/:id/editar" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'administracion']}>
                  <ReservationSettingsPage />
                </PrivateRoute>
              } />
              <Route exact path="/reservas/crear" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'administracion']}>
                  <ReservationCreatePage />
                </PrivateRoute>
              } />
              <Route exact path="/reservas/:id/ver" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'administracion',
                  'equipo_psicologia', 'doctor']}>
                  <ReservationDetailPage />
                </PrivateRoute>
              } />
              <Route exact path="/reservas/pronostico" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'administracion']}>
                  <ReservationForecastPage />
                </PrivateRoute>
              } />
              <Route exact path="/admisiones/:id" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'administracion']}>
                  <AdmissionPage />
                </PrivateRoute>
              } />
              <Route exact path="/reevaluaciones/buscar" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'administracion']}>
                  <PatientReevaluationList />
                </PrivateRoute>
              } />
              <Route exact path="/reevaluaciones/:medicalConsultationId/editar" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'administracion']}>
                  <PatientReevaluationSettings />
                </PrivateRoute>
              } />

              <Route path="/consulta-medica/buscar" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'equipo_psicologia',
                  'administracion', 'doctor', 'direccion_medica']}>
                  <MedicalConsultationPage />
                </PrivateRoute>
              } />
              <Route path="/consulta-medica/informes" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'administracion',
                  'equipo_psicologia', 'doctor', 'direccion_medica']}>
                  <MedicalConsultationReportPage />
                </PrivateRoute>
              } />

              <Route path="/consulta-medica/:medicalConsultationId" element={
                <PrivateRoute roles={['super_usuario', 'equipo_psicologia', 'doctor', 'direccion_medica',
                  'branch_office_manager']}>
                  <MedicalConsultationDetail />
                </PrivateRoute>
              } />

              <Route path="/consulta-medica" element={
                <PrivateRoute roles={['super_usuario', 'equipo_psicologia', 'doctor', 'direccion_medica',
                  'branch_office_manager']}>
                  <MedicalConsultationDetail />
                </PrivateRoute>
              } />

              <Route path="/examenes/buscar" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'equipo_medico',
                  'administracion', 'equipo_psicologia', 'doctor', 'direccion_medica', 'equipo_medico_externo']}>
                  <ExamPage />
                </PrivateRoute>
              } />
              <Route path="/examenes/firma/buscar" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'equipo_medico',
                  'equipo_psicologia', 'doctor', 'direccion_medica']}>
                  <ExamSignaturePage />
                </PrivateRoute>
              } />
              <Route path="/examenes/:paramsAdmissionPatientExamId" element={
                <PrivateRoute roles={['super_usuario', 'equipo_medico', 'equipo_psicologia',
                  'branch_office_manager', 'equipo_medico_externo']}>
                  <ExamDetailPage />
                </PrivateRoute>
              } />
              <Route path="/ingresos" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'administracion']}>
                  <PatientPage />
                </PrivateRoute>
              } />
              <Route exact path="/pacientes/buscar" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'administracion',
                  'doctor', 'direccion_medica', 'equipo_medico', 'equipo_psicologia']}>
                  <PatientsPage />
                </PrivateRoute>
              } />
              <Route exact path="/pacientes/:id/ver" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'administracion',
                  'doctor', 'direccion_medica', 'equipo_medico']}>
                  <PatientDetailPage />
                </PrivateRoute>
              } />
              <Route path="/pacientes/:id/editar" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'administracion']}>
                  <PatientSettingsPage />
                </PrivateRoute>
              } />

              <Route path="/consentimientos" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'administracion',
                  'equipo_medico', 'equipo_psicologia', 'doctor', 'direccion_medica']}>
                  <ConsentPopupPage />
                </PrivateRoute>
              } />

              <Route path="/validacion" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'administracion',
                  'equipo_medico', 'equipo_psicologia', 'doctor', 'direccion_medica']}>
                  <PatientValidationPage />
                </PrivateRoute>
              } />
              <Route path="/screenshot" element={
                <PrivateRoute roles={['super_usuario', 'branch_office_manager', 'administracion',
                  'equipo_medico', 'equipo_psicologia', 'doctor', 'direccion_medica']}>
                  <CameraPopupPage />
                </PrivateRoute>
              } />

            </Routes>
          </BrowserRouter>
        </BoxProvider>
      </I18nextProvider>
    </PermifyProvider>
  );
};

export default App;